import React, {
  Dispatch,
  PropsWithChildren,
  useContext,
  useReducer,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import { TableRef } from "../components/Shared/Table/Table";
import { Constants } from "../constants";
import useAuthorizedAction, {
  AuthorizedActionsKeys,
} from "../hooks/useAuthorizedAction";
import { IEcg, IEcgsFilter } from "../models/ecg.interface";
import ecgsService, { GetEcgsByUserIdProps } from "../services/ecgs.service";
import { apiDateFormatting } from "../utils/dateUtil";
import { useAuthContext } from "./AuthContext";

interface IEcgsTableState {
  loading: boolean;
  ecgs: IEcg[];
  selectedEcg: IEcg | null;
  tableRef: TableRef | null;
  totalEcgs: number;
  loadConfigs: Omit<GetEcgsByUserIdProps, "userId">;
}

export enum EcgsTableActionType {
  SET_LOADING = "SET_LOADING",
  SET_ECGS = "SET_ECGS",
  SET_SELECTED_ECG = "SET_SELECTED_ECG",
  SET_TABLE_REF = "SET_TABLE_REF",
  SET_TOTAL_ECGS = "SET_TOTAL_ECGS",
  // SET_FILTERS = "SET_FILTERS",
  SET_LOAD_CONFIGS = "SET_LOAD_CONFIGS",
}

type EcgsTableActions = {
  loadEcgs: (
    configs?: Omit<GetEcgsByUserIdProps, "userId">,
    reselectEcg?: boolean
  ) => Promise<void>;
};

type EcgsTableReducerAction =
  | {
      type: EcgsTableActionType.SET_LOADING;
      payload: boolean;
    }
  | {
      type: EcgsTableActionType.SET_ECGS;
      payload: IEcg[];
    }
  | {
      type: EcgsTableActionType.SET_TABLE_REF;
      payload: TableRef;
    }
  | {
      type: EcgsTableActionType.SET_SELECTED_ECG;
      payload: IEcg | null;
    }
  | {
      type: EcgsTableActionType.SET_TOTAL_ECGS;
      payload: number;
    }
  | {
      type: EcgsTableActionType.SET_LOAD_CONFIGS;
      payload: Omit<GetEcgsByUserIdProps, "userId">;
    };

interface IEcgsTableContext {
  state: IEcgsTableState;
  dispatch: Dispatch<EcgsTableReducerAction>;
  actions: EcgsTableActions;
}

const initState = (isReviewer?: boolean): IEcgsTableState => ({
  loading: false,
  ecgs: [],
  selectedEcg: null,
  tableRef: null,
  totalEcgs: 0,
  loadConfigs: {
    pageIndex: 0,
    pageSize: Constants.TABLE_ITEMS_PER_PAGE,
    filters: isReviewer ? { unInterpretedOnly: true } : {},
  },
});

const EcgsTableContext = React.createContext<IEcgsTableContext>({
  state: initState(),
  dispatch: () => {},
  actions: { loadEcgs: () => new Promise((resolve) => resolve()) },
});

const ecgsTableReducer = (
  state: IEcgsTableState,
  action: EcgsTableReducerAction
): IEcgsTableState => {
  switch (action.type) {
    case EcgsTableActionType.SET_LOADING:
      return { ...state, loading: action.payload };
    case EcgsTableActionType.SET_ECGS:
      return { ...state, ecgs: action.payload };
    case EcgsTableActionType.SET_SELECTED_ECG:
      return { ...state, selectedEcg: action.payload };
    case EcgsTableActionType.SET_TABLE_REF:
      return { ...state, tableRef: action.payload };
    case EcgsTableActionType.SET_TOTAL_ECGS:
      return { ...state, totalEcgs: action.payload };
    case EcgsTableActionType.SET_LOAD_CONFIGS:
      return { ...state, loadConfigs: action.payload };
    default:
      return state;
  }
};

type EcgsTableContextProviderProps = PropsWithChildren<{}>;

enum TableNavDirections {
  Prev,
  Next,
}

export const EcgsTableContextProvider = ({
  children,
}: EcgsTableContextProviderProps) => {
  const {
    state: { user },
  } = useAuthContext();

  const isReviewer = useAuthorizedAction(AuthorizedActionsKeys.ReviewEcg);

  const [state, dispatch] = useReducer(ecgsTableReducer, initState(isReviewer));

  const [searchParams] = useSearchParams();

  const [fetchedPages, setFetchedPages] = useState<number[]>([]);
  const [lastAppliedFilters, setLastAplliedFilters] = useState<
    IEcgsFilter | undefined
  >({});

  const actions: EcgsTableActions = {
    loadEcgs: async (configs = state.loadConfigs, reselectEcg = true) => {
      try {
        const { selectedEcg } = state;

        dispatch({
          type: EcgsTableActionType.SET_LOADING,
          payload: true,
        });

        dispatch({
          type: EcgsTableActionType.SET_SELECTED_ECG,
          payload: null,
        });

        const uniqueId = searchParams.get(Constants.UNIQUE_ID_QUERY_PARAM_NAME);

        if ((uniqueId || uniqueId === "") && configs.filters)
          configs.filters.uniqueId = uniqueId;

        //DO NOT LOAD NEW ECGS IF PAGE INDEX IS ALREADY FETCHED AND IF ALL FILTERS ARE THE SAME
        if (
          configs.pageAmount &&
          fetchedPages.includes(configs.pageIndex) &&
          JSON.stringify(configs.filters) === JSON.stringify(lastAppliedFilters)
        )
          return;

        let direction = fetchedPages.length
          ? Math.min(...fetchedPages) > configs.pageIndex
            ? TableNavDirections.Prev
            : TableNavDirections.Next
          : TableNavDirections.Next;

        const { success, data } = await ecgsService.getEcgsByUserId({
          userId: user!.id,
          ...configs,
          pageIndex:
            direction === TableNavDirections.Prev
              ? configs.pageIndex - (configs.pageAmount! - 1)
              : configs.pageIndex,
          filters: {
            ...configs.filters,
            fromDate: configs.filters?.fromDate
              ? apiDateFormatting(configs.filters?.fromDate)
              : undefined,
            toDate: configs.filters?.toDate
              ? apiDateFormatting(configs.filters?.toDate)
              : undefined,
          },
        });

        if (!success || !data) {
          return;
        }

        const newFetchedPages: number[] = [];
        if (
          data.actualCount &&
          data.actualCount < configs.pageAmount! * configs.pageSize + 1
        ) {
          //CALCULATE HOW MANY PAGES LEFT
          const pagesLeft = Math.ceil(data.actualCount / configs.pageSize);
          for (let i = 0; i < pagesLeft; i++)
            newFetchedPages.push(configs.pageIndex + i);
        } else {
          for (let i = 0; i < (configs.pageAmount || 0); i++)
            newFetchedPages.push(
              direction === TableNavDirections.Next
                ? configs.pageIndex + i
                : configs.pageIndex - i
            );
        }
        setFetchedPages(newFetchedPages);
        setLastAplliedFilters(configs.filters);

        const { ecgs, actualCount } = data;

        dispatch({
          type: EcgsTableActionType.SET_ECGS,
          payload: ecgs,
        });

        dispatch({
          type: EcgsTableActionType.SET_TOTAL_ECGS,
          payload: actualCount || 0,
        });

        if (reselectEcg && selectedEcg) {
          const ecg = ecgs.find((ecg) => ecg.id === selectedEcg.id) ?? null;
          if (ecg) {
            dispatch({
              type: EcgsTableActionType.SET_SELECTED_ECG,
              payload: ecg,
            });
          }
        }
      } finally {
        dispatch({
          type: EcgsTableActionType.SET_LOADING,
          payload: false,
        });
      }
    },
  };

  return (
    <EcgsTableContext.Provider value={{ state, dispatch, actions }}>
      {children}
    </EcgsTableContext.Provider>
  );
};

export const useEcgsTableContext = () => useContext(EcgsTableContext);
